import { useEffect, useState } from "react";
import { FamilyInfoForCard } from "../../components/CRM/contacts/ContactPortalAccessCard";
import { contactPortalAgentService } from "../../services/crm/contactPortalAgentService";
import { useFamilyById } from "../crm/useFamilies";

/**
 * Resolves the (non-CLOSED) family a single contact belongs to, plus its eligible
 * (non-DELETED) fellow members — computed once here so UserConsultingPage/ConsultingHeader/
 * ContactPortalAccessCard don't each independently re-fetch the same relationship.
 *
 * Returns null while loading or when the contact has no active family.
 */
export const useContactFamilyInfo = (
  contactId: number | undefined
): { familyInfo: FamilyInfoForCard | null; isLoading: boolean } => {
  const [familyId, setFamilyId] = useState<number | null>(null);
  const [siblingContacts, setSiblingContacts] = useState<FamilyInfoForCard["siblingContacts"] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!contactId) {
      setFamilyId(null);
      setSiblingContacts(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    contactPortalAgentService
      .getEligibleFamilyMembers(contactId)
      .then((members) => {
        setSiblingContacts(members);
        setFamilyId(members.length > 0 ? members[0].family_id : null);
      })
      .catch(() => {
        setSiblingContacts(null);
        setFamilyId(null);
      })
      .finally(() => setIsLoading(false));
  }, [contactId]);

  const { data: family } = useFamilyById(familyId ?? undefined);

  if (!familyId || !siblingContacts || !family) {
    return { familyInfo: null, isLoading };
  }

  return {
    familyInfo: { familyId, familyName: family.name, siblingContacts },
    isLoading,
  };
};
